import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import LineWhatsappIcon from 'LuxKit/Icons/line/LineWhatsappIcon'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import OfficeLocationModal, { OFFICE_LOCATION_CONTENT, OfficeLocations } from 'components/Pages/AboutUsPage/OfficeLocationModal/OfficeLocationModal'
import Group from 'components/utils/Group'
import config from 'constants/config'
import { NO_24_7_SUPPORT_REGIONS, PARTNER_BRANDS_ENABLED_REGIONS } from 'constants/config/region'
import ModalContext from 'contexts/ModalContext'
import GeoContext from 'contexts/geoContext'
import { useRenderWhatsApp } from 'hooks/useRenderWhatsApp'
import { getSVGImageUrl } from 'lib/image/imageUtils'
import shouldRenderTimely, { getTimelyLink } from 'lib/timely/timely'
import getStaticPagesByType from 'lib/utils/getStaticPagesByType'
import { rem } from 'polished'
import React, { useCallback, useContext, useMemo, useRef } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import { useIntl, FormattedMessage } from 'react-intl'
import Accreditations from './Accreditations'
import FooterItemList from './FooterItemList'
import FooterListItem from './FooterListItem'
import FooterListItemsGrid from './FooterListItemsGrid'
import FooterList from './FooterList'
import LineCommentsIcon from 'LuxKit/Icons/line/LineCommentsIcon'
import { useAppSelector } from 'hooks/reduxHooks'
import { isSupportAssistantEnabled } from 'selectors/featuresSelectors'
import { useAppFooterObserver } from 'hooks/useAppLayoutObservers'
import { STORE_CAMPAIGN_URL } from 'constants/retailStores'

const Container = styled(Pane)`
  padding-block: ${rem(40)};
`

const StoreLocation = styled(BodyText)`
  padding-inline: ${rem(8)};
`

interface Props {
  isStoreMode: boolean
  region: string
}

function BottomSection(props: Props) {
  const { isStoreMode } = props
  const intl = useIntl()

  const currentYear = new Date().getFullYear()
  const { currentRegionCode } = useContext(GeoContext)
  const { offices } = OFFICE_LOCATION_CONTENT
  const showOtherPartners = !isStoreMode && PARTNER_BRANDS_ENABLED_REGIONS.includes(currentRegionCode)

  const shouldRenderWhatsApp = useRenderWhatsApp(currentRegionCode)

  const showModal = useContext(ModalContext)
  const toggleOfficeModal = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const selectedOfficeLocation = e.currentTarget.dataset.key as OfficeLocations
    showModal(<OfficeLocationModal selectedOffice={selectedOfficeLocation} />)
  }, [showModal])

  // Separate retail stores and offices
  const retailStores = offices.filter(
    office => office.location === OfficeLocations.BONDI || office.location === OfficeLocations.CHADSTONE,
  )
  const officeLocations = offices.filter(
    office => office.location !== OfficeLocations.BONDI && office.location !== OfficeLocations.CHADSTONE,
  )

  const showAtolProtection = currentRegionCode === 'GB'

  const supportAssistantEnabled = useAppSelector(isSupportAssistantEnabled)
  const shouldShowSupportSection = supportAssistantEnabled || shouldRenderWhatsApp || shouldRenderTimely(currentRegionCode)

  const containerRef = useRef<HTMLDivElement>(null)
  useAppFooterObserver(containerRef)

  const getPageText = (pageType: string): string => {
    const pageTextMap: Record<string, string> = {
      aboutUs: intl.formatMessage({
        id: 'FU9oWPdrcR',
        defaultMessage: 'About Us',
        description: 'About Us page link text in footer',
      }),
      bestPrice: intl.formatMessage({
        id: 'ncv6twKYWk',
        defaultMessage: 'Best Price Guarantee',
        description: 'Best Price Guarantee page link text in footer',
      }),
      careersAndCulture: intl.formatMessage({
        id: 'FBLsoYphBP',
        defaultMessage: 'Careers & Culture',
        description: 'Careers & Culture page link text in footer',
      }),
      affiliatePage: intl.formatMessage({
        id: 'lWQYQHHkLj',
        defaultMessage: 'Become an Affiliate Partner',
        description: 'Become an Affiliate Partner page link text in footer',
      }),
      termsAndConditions: intl.formatMessage({
        id: '374mOFasMs',
        defaultMessage: 'Terms & Conditions',
        description: 'Terms & Conditions page link text in footer',
      }),
      privacyPolicy: intl.formatMessage({
        id: 'el2Htoyn+W',
        defaultMessage: 'Privacy Policy',
        description: 'Privacy Policy page link text in footer',
      }),
      giftcardTermsAndConditions: intl.formatMessage({
        id: 'FnMuoaWkeo',
        defaultMessage: 'Luxury Escapes Gift Cards',
        description: 'Gift Cards page link text in footer',
      }),
      refundPolicy: intl.formatMessage({
        id: 'oQ+14OMooR',
        defaultMessage: 'Refund Policy',
        description: 'Refund Policy page link text in footer',
      }),
      support: intl.formatMessage({
        id: '4jaOy4YxTy',
        defaultMessage: 'Support',
        description: 'Support page link text in footer',
      }),
    }
    return pageTextMap[pageType] || ''
  }

  const luxuryEscapesItems = getStaticPagesByType(['aboutUs', 'bestPrice', 'careersAndCulture', 'affiliatePage'])
    .filter(page => !!page.url)
    .map(page => ({ ...page, text: getPageText(page.type) }))

  const helpItems = getStaticPagesByType(['termsAndConditions', 'privacyPolicy', 'giftcardTermsAndConditions', 'refundPolicy', 'support'])
    .filter(page => !!page.url)
    .map(page => ({ ...page, text: getPageText(page.type) }))

  const otherPartners = getStaticPagesByType(['scoopon', 'cudo', 'deals', 'treatme', 'catch', 'travelshoot']).filter(
    page => !!page.url,
  )

  const customerSupportTitle = useMemo(
    () => {
      if (NO_24_7_SUPPORT_REGIONS.includes(currentRegionCode)) {
        return intl.formatMessage({
          id: 'I4MSgXI91n',
          defaultMessage: 'Customer support',
          description: 'Title for customer support section in footer',
        })
      }
      return intl.formatMessage({
        id: 'ao08p1obmr',
        defaultMessage: '24-7 Customer support',
        description: 'Title for 24-7 customer support section in footer',
      })
    },
    [intl, currentRegionCode],
  )

  const chatNowTitle = intl.formatMessage({
    id: 'CiNyC0Jk1u',
    defaultMessage: 'Chat now',
    description: 'Link text to start chat support',
  })

  const available247Subtitle = intl.formatMessage({
    id: 'DcKjsn5ptR',
    defaultMessage: 'Available 24/7',
    description: 'Subtitle indicating 24/7 availability for chat support',
  })

  const whatsappTitle = 'WhatsApp'

  const whatsappAriaLabel = 'Whats App'

  const bookStoreAppointmentTitle = intl.formatMessage({
    id: 'T07zQEwxNM',
    defaultMessage: 'Book store appointment',
    description: 'Link text to book store appointment',
  })

  const timelyBookingAriaLabel = intl.formatMessage({
    id: '/MC8wakaW3',
    defaultMessage: 'Timely booking',
    description: 'Aria label for Timely booking link',
  })

  const luxuryEscapesComTitle = 'LuxuryEscapes.com'

  const helpTitle = intl.formatMessage({
    id: 'JFWqiVwfAG',
    defaultMessage: 'Help',
    description: 'Title for help links section in footer',
  })

  const ourPartnerBrandsTitle = intl.formatMessage({
    id: 'UHOrGhx0Ax',
    defaultMessage: 'Our Partner Brands',
    description: 'Title for partner brands section in footer',
  })

  const luxuryEscapesLocationsTitle = intl.formatMessage({
    id: '+876cvdFdO',
    defaultMessage: 'Luxury Escapes Locations',
    description: 'Title for Luxury Escapes locations section in footer',
  })

  const ourRetailStoresTitle = intl.formatMessage({
    id: 'VSAZa0Bb0P',
    defaultMessage: 'Our Retail Stores',
    description: 'Title for retail stores section in footer',
  })

  const ourOfficesTitle = intl.formatMessage({
    id: '3s/5NsgyEU',
    defaultMessage: 'Our Offices',
    description: 'Title for offices section in footer',
  })

  const luxuryEscapesStoreTitle = intl.formatMessage({
    id: 'AMgAcmSBJ9',
    defaultMessage: 'Luxury Escapes Store',
    description: 'Title for Luxury Escapes store section in footer',
  })

  const atolProtectionTitle = intl.formatMessage({
    id: 'xmhIuMkpPm',
    defaultMessage: 'ATOL Protection',
    description: 'Title for ATOL protection section in footer',
  })

  const copyrightText = useMemo(
    () =>
      intl.formatMessage(
        {
          id: '1m1R/mic4N',
          defaultMessage: 'Copyright © 2017 - {year} Luxury Escapes.',
          description: 'Copyright notice in footer with year',
        },
        {
          year: currentYear,
        },
      ),
    [intl, currentYear],
  )

  const allRightsReservedText = intl.formatMessage({
    id: 'seD0OPcWY/',
    defaultMessage: 'All rights reserved',
    description: 'All rights reserved text in footer',
  })

  return <Container background="neutral-six" className="hidden-print" ref={containerRef}>
    <LayoutContainer variant="large">
      <VerticalSpacer gap={56}>
        <FooterListItemsGrid>
          {shouldShowSupportSection && <FooterList title={customerSupportTitle}>
            {supportAssistantEnabled && <FooterListItem
              to="/support"
              rel="nofollow"
              title={chatNowTitle}
              subtitle={available247Subtitle}
              startIcon={<LineCommentsIcon />}
            />}
            {shouldRenderWhatsApp && <FooterListItem
              title={whatsappTitle}
              startIcon={<LineWhatsappIcon />}
              href={`https://wa.me/${config.WHATS_APP_NUMBER}`}
              aria-label={whatsappAriaLabel}
              rel="noopener"
              target="_blank"
            />}
            {shouldRenderTimely(currentRegionCode) && <FooterListItem
              title={bookStoreAppointmentTitle}
              startIcon={
                <img
                  src={getSVGImageUrl('logo-LE')}
                  width="24"
                  height="24"
                  alt={intl.formatMessage({
                    id: 'b4I1xOsrGt',
                    defaultMessage: 'LE store',
                    description: 'Alt text for Luxury Escapes store logo in footer',
                  })}
                />
              }
              href={getTimelyLink()}
              aria-label={timelyBookingAriaLabel}
              rel="noopener"
              target="_blank"
            />}
          </FooterList>}
          <FooterItemList title={luxuryEscapesComTitle} items={luxuryEscapesItems} />
          <FooterItemList title={helpTitle} items={helpItems} />
          {showOtherPartners && <FooterItemList title={ourPartnerBrandsTitle} items={otherPartners} />}
          {isStoreMode && <FooterItemList title={luxuryEscapesLocationsTitle} items={helpItems} />}
          {!isStoreMode && <Group direction="vertical" gap={24}>
            {shouldRenderTimely(currentRegionCode) && <FooterList title={ourRetailStoresTitle}>
              {retailStores.map((store) => (
                <FooterListItem
                  key={store.location}
                  title={store.location}
                  to={STORE_CAMPAIGN_URL}
                />
              ))}
            </FooterList>}
            <FooterList title={ourOfficesTitle}>
              {officeLocations.map((office) => <FooterListItem
                key={office.location}
                title={office.location}
                data-key={office.location}
                onClick={toggleOfficeModal}
              />)}
            </FooterList>
          </Group>}
          {isStoreMode && <FooterList title={luxuryEscapesStoreTitle}>
            <StoreLocation variant="medium">
              <FormattedMessage
                id="P7scfD178v"
                defaultMessage="Shop B-186, Chadstone Shopping Centre{br}1341 Dandenong Road,{br}Chadstone VIC 3148"
                description="Full store address in footer - shop number, street and suburb"
                values={{
                  br: <br />,
                }}
              />
              {currentRegionCode === 'AU' && <><br /><br />{intl.formatMessage({
                id: 'yTkPG/nRFU',
                defaultMessage: 'Australian owned and operated.',
                description: 'Text indicating the company is Australian owned, shown for AU region in footer',
              })}
              </>}
            </StoreLocation>
          </FooterList>}
        </FooterListItemsGrid>
        {showAtolProtection && <VerticalSpacer gap={12}>
          <BodyText variant="large" weight="bold">{atolProtectionTitle}</BodyText>
          <BodyText variant="medium" align="justify">
            <FormattedMessage
              id="oEk6HtbWwY"
              defaultMessage="Some of the flights and flight-inclusive holidays on this website are financially protected by the ATOL scheme. But ATOL protection does not apply to all holiday and travel services listed on this website. This website will provide you with information on the protection that applies in the case of each holiday and travel service offered before you make your booking. If you do not receive an ATOL Certificate then the booking will not be ATOL protected. If you do receive an ATOL Certificate but all the parts of your trip are not listed on it, those parts will not be ATOL protected. Please see our booking conditions for information, or for more information about financial protection and the ATOL Certificate go to: <link>www.caa.co.uk</link>"
              description="ATOL protection information text with link to CAA website"
              values={{
                link: (chunks: React.ReactNode) => (
                  <TextLink key="caa-link" weight="regular" href="https://www.caa.co.uk/" target="_blank">
                    {chunks}
                  </TextLink>
                ),
              }}
            />
          </BodyText>
        </VerticalSpacer>}
        <Divider kind="primary" />
        <Group
          direction="vertical"
          gap={32}
          tabletDirection="horizontal"
          tabletVerticalAlign="center"
          tabletHorizontalAlign="space-between"
        >
          {!isStoreMode && <Accreditations
            showProductReviewWinner
          />}
          <BodyText variant="small" align="center" tabletAlign="end">
            <small>{copyrightText} {allRightsReservedText}</small>
          </BodyText>
        </Group>
      </VerticalSpacer>
    </LayoutContainer>
  </Container>
}

function mapStateToProps(state: App.State) {
  return {
    isStoreMode: state.system.storeMode,
    region: state.geo.currentRegionCode,
  }
}

export default connect(mapStateToProps)(BottomSection)
